import { SText } from '@setvi/shared/components';
import { STextProps } from '@setvi/shared/components/sui/stext';
import { ProductInventory, StockInfo } from '@setvi/shared/interfaces';

export interface StockedProps extends Omit<STextProps, 'title'> {
  row: ProductInventory;
  stockInfo?: StockInfo;
}

const normalize = (value?: string) => value?.trim().toLowerCase() || '';

/**
 * Stocking policy for a single inventory row (DEV-797). `stockInfo.warehouses`
 * is keyed by the ERP warehouse code, which is what an inventory row carries as
 * `warehouseId` - the warehouse name is matched as a fallback because Singer
 * stores both as the same "ELVERSON-PA (001)" string.
 *
 * `allWarehouses` means the product is stocked everywhere, so every row is
 * stocked. A row with no matching entry in `stockInfo` is reported as not
 * stocked, mirroring the product level badge in getStockInfo.
 */
export const isWarehouseStocked = (
  row: ProductInventory,
  stockInfo?: StockInfo
) => {
  if (stockInfo?.allWarehouses) return true;

  const keys = [row?.warehouseId, row?.warehouse?.code, row?.warehouse?.name]
    .map(normalize)
    .filter(Boolean);

  return (
    stockInfo?.warehouses?.some(
      warehouse =>
        warehouse?.stock &&
        (keys.includes(normalize(warehouse.warehouseId)) ||
          keys.includes(normalize(warehouse.warehouseName)))
    ) || false
  );
};

export const Stocked = ({ row, stockInfo, ...rest }: StockedProps) =>
  stockInfo ? (
    <SText
      size="sm"
      lineHeight="20px"
      title={isWarehouseStocked(row, stockInfo) ? 'Stocked' : 'Not stocked'}
      {...rest}
    />
  ) : (
    '-'
  );
